import React from "react";
import {
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Grid,
  Avatar,
} from "@mui/material";
import { WarningAmber } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import useFetchObjects from "../../../../api/useFetchObjects";
import { useAuth } from "../../../../context";
import { StockMedicineSkeleton } from "./loading/StockMedicineSkeleton";

const LowQuantityStockMedicine = () => {
  const { t } = useTranslation();
  const auth = useAuth();
  const token = auth?.user?.token;
  const pharmacy = auth?.user?.user?.pharmacy_type;

  // If pharmacy is not set, show a message (or handle it as needed)
  if (!pharmacy) {
    return (
      <Paper
        sx={{
          boxShadow: "0px 8px 24px rgba(0,0,0,0.1)",
          borderRadius: "16px",
          marginTop: "2rem",
          padding: { xs: "1.5rem", md: "2.5rem" },
          width: "100%",
        }}
      >
        <Typography variant="h6" align="center" sx={{ mt: 4 }}>
          {t("pharmacy.noPharmacySelected", "No pharmacy is selected.")}
        </Typography>
      </Paper>
    );
  }

  // Build the endpoint conditionally
  const endpoint = pharmacy
    ? `low-quantity-stock/?pharmacy=${pharmacy}`
    : "low-quantity-stock/";

  const {
    data: lowQuantityStockData,
    isError: isErrorLowQuantityStock,
    error: errorLowQuantityStock,
  } = useFetchObjects(["low-quantity-stock"], endpoint, token);

  // While waiting for data, show a skeleton loader
  if (!lowQuantityStockData) {
    return <StockMedicineSkeleton />;
  }

  // If there is an error, show the error message
  if (isErrorLowQuantityStock) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Typography color="error">
          {errorLowQuantityStock.message}
        </Typography>
      </Box>
    );
  }

  // If data is returned but empty, show a no data message
  if (lowQuantityStockData.length === 0) {
    return (
      <Paper
        sx={{
          boxShadow: "0px 8px 24px rgba(0,0,0,0.1)",
          borderRadius: "16px",
          marginTop: "2rem",
          padding: { xs: "1.5rem", md: "2.5rem" },
          width: "100%",
        }}
      >
        <Typography variant="h6" align="center" sx={{ mt: 4 }}>
          {t("pharmacy.noLowQuantityStockFound", "No low quantity stock found.")}
        </Typography>
      </Paper>
    );
  }

  return (
    <Paper
      sx={{
        boxShadow: "0px 8px 24px rgba(0,0,0,0.1)",
        borderRadius: "16px",
        marginTop: "2rem",
        padding: { xs: "1.5rem", md: "2.5rem" },
        width: "100%",
      }}
    >
      {/* Header Section */}
      <Grid container alignItems="center" spacing={2}>
        <Grid item>
          <Avatar sx={{ bgcolor: "#d32f2f", width: 56, height: 56 }}>
            <WarningAmber fontSize="large" />
          </Avatar>
        </Grid>
        <Grid item>
          <Typography fontWeight={700} variant="h4" color="error">
            {t("overview.lowQuantityStock", "Low Quantity Stock")}
          </Typography>
        </Grid>
      </Grid>

      {/* Table Section */}
      <TableContainer component={Box} sx={{ mt: 4 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#f5f5f5" }}>
              <TableCell align="center">
                <Typography fontWeight={700} color="primary">
                  {t("pharmacy.category", "Category")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700} color="primary">
                  {t("pharmacy.medicineName", "Medicine Name")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700} color="primary">
                  {t("pharmacy.expiryDate", "Expiry Date")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700} color="primary">
                  {t("pharmacy.quantity", "Quantity")}
                </Typography>
              </TableCell>
              <TableCell align="center">
                <Typography fontWeight={700} color="primary">
                  {t("pharmacy.sellPrice", "Sell Price")} (؋)
                </Typography>
              </TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {lowQuantityStockData.map((stock, index) => (
              <TableRow
                key={index}
                sx={{
                  "&:hover": { backgroundColor: "#f9f9f9" },
                  "&:nth-of-type(even)": { backgroundColor: "#fdfdfd" },
                }}
              >
                <TableCell align="center">
                  <Typography>{stock.category_display}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>{stock.medicine_display}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>{stock.expiry_date}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>{stock.quantity}</Typography>
                </TableCell>
                <TableCell align="center">
                  <Typography>{stock.sell_price} ؋</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
};

export default LowQuantityStockMedicine;
